import { useEffect, useState } from 'react';
import { useQueryClient } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';

import { QueryKeyEnum } from 'core/global.enum';
import { PaginatedResponse } from 'core/global.types';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { fetchTerminals, updateTerminals } from 'api/terminals/terminals';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useMutation } from 'hooks/useMutation/useMutation';
import { TerminalIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { useModalState } from 'hooks/useModalState/useModalState';
import { Server } from 'api/servers/servers.types';

import { useAddTerminalsDisplayValidation } from './AddTerminalsDisplay.validation';
import { AddTerminalsDisplayFormBody } from './AddTerminalsDisplay.types';
import { AddTerminalsDisplay } from './AddTerminalsDisplay';

export const AddTerminalsDisplayContainer = () => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const { schema } = useAddTerminalsDisplayValidation();

  const [selectedServerId, setSelectedServerId] = useState('');

  const servers = queryClient.getQueryData<PaginatedResponse<Server>>([QueryKeyEnum.SERVERS])?.data || [];

  useEffect(() => {
    servers.length && setSelectedServerId(servers[0].id);
  }, [servers]);

  const methods = useForm<AddTerminalsDisplayFormBody>({
    defaultValues: {
      idTkds: [],
    },
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, selectedServerId],
    () => fetchTerminals(selectedServerId),
    {
      args: {
        zaakceptowane: true,
        sort: [TerminalIdSortEnum.ASC],
      },
      enabled: !!selectedServerId,
    },
  );

  const { mutate: onUpdateTerminals, isLoading } = useMutation(updateTerminals, {
    onSuccess: (_, { idTkds }) => {
      queryClient.invalidateQueries([QueryKeyEnum.TERMINALS_WITH_DISPLAY]);

      idTkds?.length > 1
        ? toast.dark(formatMessage({ id: AppMessages['definitions.display.add.terminal.success.multi'] }))
        : toast.dark(formatMessage({ id: AppMessages['definitions.display.add.terminal.success'] }));

      onModalClose();
    },
  });

  const onSubmit = (body: AddTerminalsDisplayFormBody) => {
    onUpdateTerminals({
      idTkds: body.idTkds.map(({ value }) => `${value}`),
      wyswietlacz: true,
    });
  };

  useEffect(() => {
    methods.reset();
  }, [isModalOpen]);

  return (
    <FormProvider {...methods}>
      <AddTerminalsDisplay
        selectedServerId={selectedServerId}
        setSelectedServerId={setSelectedServerId}
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
        isLoading={isLoading}
        isLoadingTerminals={isLoadingTerminals}
        terminals={terminalsResponse?.data || []}
        servers={servers}
      />
    </FormProvider>
  );
};
